<template>
  <Transition name="dialog">
    <div
      v-if="show"
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-6"
      @click.self="handleCancel"
    >
      <div class="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl animate-in fade-in zoom-in-95 duration-200">
        <!-- 標題 -->
        <h3 class="text-lg font-bold text-textMain mb-4">驗證身份</h3>

        <!-- 說明 -->
        <p class="text-xs text-textMuted mb-4">
          為了安全性，刪除帳號前需要重新驗證您的身份。請輸入密碼以繼續。
        </p>

        <!-- 表單 -->
        <div class="space-y-4">
          <!-- Password -->
          <div>
            <label class="block text-sm font-bold text-textMain mb-2">密碼</label>
            <div class="relative">
              <input
                v-model="password"
                :type="showPassword ? 'text' : 'password'"
                placeholder="請輸入您的密碼"
                class="w-full px-4 py-3 pr-12 rounded-xl bg-gray-50 border border-gray-200 focus:border-primary focus:outline-none transition-colors"
                :class="{ 'border-red-400': passwordError }"
                @keyup.enter="handleSubmit"
              />
              <button
                type="button"
                @click="showPassword = !showPassword"
                class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <!-- 睁开的眼睛 -->
                <svg v-if="showPassword" xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
                <!-- 闭上的眼睛 -->
                <svg v-else xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                  <line x1="1" y1="1" x2="23" y2="23"></line>
                </svg>
              </button>
            </div>
            <p v-if="passwordError" class="text-xs text-red-500 mt-1">{{ passwordError }}</p>
          </div>
        </div>

        <!-- 錯誤訊息 -->
        <div v-if="generalError" class="mt-4 p-3 bg-red-50 rounded-xl">
          <p class="text-xs text-red-600">{{ generalError }}</p>
        </div>

        <!-- 按鈕 -->
        <div class="flex gap-3 mt-6">
          <button
            @click="handleCancel"
            class="flex-1 py-3 rounded-xl font-bold text-textMuted bg-gray-100 active:scale-95 transition-all"
            :disabled="loading"
          >
            取消
          </button>
          <button
            @click="handleSubmit"
            class="flex-1 py-3 rounded-xl font-bold text-white bg-red-500 active:scale-95 transition-all disabled:opacity-50"
            :disabled="loading || !password"
          >
            {{ loading ? '驗證中...' : '確認刪除' }}
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref } from 'vue'

interface Props {
  show: boolean
}

defineProps<Props>()

const emit = defineEmits<{
  confirm: [password: string]
  cancel: []
}>()

const password = ref('')
const loading = ref(false)
const showPassword = ref(false)
const passwordError = ref('')
const generalError = ref('')

const handleSubmit = () => {
  if (!password.value) {
    passwordError.value = '請輸入密碼'
    return
  }

  passwordError.value = ''
  generalError.value = ''
  loading.value = true
  emit('confirm', password.value)
}

const handleCancel = () => {
  if (loading.value) return
  resetForm()
  emit('cancel')
}

const resetForm = () => {
  password.value = ''
  showPassword.value = false
  passwordError.value = ''
  generalError.value = ''
  loading.value = false
}

const setError = (error: string) => {
  generalError.value = error
  loading.value = false
}

const setLoading = (value: boolean) => {
  loading.value = value
}

defineExpose({
  setError,
  resetForm,
  setLoading
})
</script>

<style scoped>
.dialog-enter-active,
.dialog-leave-active {
  transition: opacity 0.2s ease;
}

.dialog-enter-from,
.dialog-leave-to {
  opacity: 0;
}

@keyframes fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes zoom-in-95 {
  from {
    transform: scale(0.95);
  }
  to {
    transform: scale(1);
  }
}

.animate-in {
  animation: fade-in 0.2s ease, zoom-in-95 0.2s ease;
}
</style>
